import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Select, MenuItem, FormControl,
  InputLabel, Paper, Alert, Autocomplete, Chip, Dialog, DialogTitle,
  DialogContent, List, ListItem, ListItemText, Checkbox, DialogActions,
} from '@mui/material';
import { Save, Cancel, Add, ViewColumn, Groups } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { RendimientoService } from '../services/rendimientoService';
import { PeriodoService } from '../../periodos/services/periodoService';
import { TrabajadorService } from '../../trabajadores/services/trabajadorService';
import type {
  Actividad, Labor, Consumidor, GrupoTrabajo,
  AsistenciaRendimiento, SaveRendimientoRequest, SaveRendimientoDetalle, SaveProduccion,
} from '../types/rendimiento';
import type { PeriodoPlanilla } from '../../periodos/types/periodo.types';
import type { Trabajador } from '../../trabajadores/types/trabajador.types';
import { ESTADOS_RENDIMIENTO } from '../types/rendimiento';
import RendimientoDetalleGrid from './RendimientoDetalleGrid';
import GrupoTrabajadorDialog from './GrupoTrabajadorDialog';
import { AsistenciaService } from '../../asistencia/services/asistenciaService';
import type { Asistencia } from '../../asistencia/types/asistencia';
import http from '../../../../api/http';

interface TurnoOption {
  idTurno: number;
  descripcion: string;
}

const RendimientoFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = id && id !== 'nuevo';

  // Master data
  const [periodos, setPeriodos] = useState<PeriodoPlanilla[]>([]);
  const [trabajadores, setTrabajadores] = useState<Trabajador[]>([]);
  const [actividades, setActividades] = useState<Actividad[]>([]);
  const [labores, setLabores] = useState<Labor[]>([]);
  const [consumidores, setConsumidores] = useState<Consumidor[]>([]);
  const [grupos, setGrupos] = useState<GrupoTrabajo[]>([]);
  const [turnos, setTurnos] = useState<TurnoOption[]>([]);

  // Form state
  const [idPeriodo, setIdPeriodo] = useState<number>(0);
  const [fecha, setFecha] = useState<string>(new Date().toISOString().split('T')[0]);
  const [idResponsable, setIdResponsable] = useState<number | null>(null);
  const [responsableObj, setResponsableObj] = useState<Trabajador | null>(null);
  const [idTurno, setIdTurno] = useState<number | null>(null);
  const [idGrupoTrabajo, setIdGrupoTrabajo] = useState<number | null>(null);
  const [observaciones, setObservaciones] = useState<string>('');
  const [estado, setEstado] = useState<string>('PENDIENTE');
  const [detalles, setDetalles] = useState<SaveRendimientoDetalle[]>([]);
  const [consumidoresSeleccionados, setConsumidoresSeleccionados] = useState<Consumidor[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);
  const [addWorkerOpen, setAddWorkerOpen] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState<Trabajador | null>(null);
  const [consumidorDialogOpen, setConsumidorDialogOpen] = useState(false);
  const [grupoTrabajadorDialogOpen, setGrupoTrabajadorDialogOpen] = useState(false);
  const [asistencias, setAsistencias] = useState<Asistencia[]>([]);

  useEffect(() => {
    loadMasterData();
  }, []);

  useEffect(() => {
    if (isEdit) loadDocumento(Number(id));
  }, [id]);

  // Load attendance when periodo changes
  useEffect(() => {
    if (idPeriodo) {
      AsistenciaService.getByPeriodo(idPeriodo).then(setAsistencias).catch(() => setAsistencias([]));
    }
  }, [idPeriodo]);

  // Auto-load workers when grupo changes (new documents only)
  useEffect(() => {
    if (!idGrupoTrabajo || isEdit) return;
    const loadGroupWorkers = async () => {
      try {
        const miembros = await RendimientoService.getTrabajadoresGrupo(idGrupoTrabajo);
        if (miembros.length === 0) return;

        const existingIds = new Set(detalles.map(d => d.idTrabajador));
        const newWorkerIds = miembros
          .filter(m => m.activo && !existingIds.has(m.idTrabajador))
          .map(m => m.idTrabajador);

        if (newWorkerIds.length === 0) return;

        const defaultActividad = actividades.find(a => a.activo);
        const defaultLabores = defaultActividad
          ? labores.filter(l => l.idActividad === defaultActividad.idActividad && l.activo)
          : [];

        let nextItem = detalles.length > 0 ? Math.max(...detalles.map(d => d.item)) + 1 : 1;
        const newDetalles: SaveRendimientoDetalle[] = [];

        for (const idTrab of newWorkerIds) {
          const horas = getHorasTrabajadas(idTrab);
          const workerLabores = defaultLabores.slice(0, 3);

          if (workerLabores.length > 0) {
            for (const lab of workerLabores) {
              newDetalles.push({
                idDetalle: 0,
                idTrabajador: idTrab,
                idActividad: defaultActividad?.idActividad || 0,
                idLabor: lab.idLabor,
                item: nextItem++,
                tipoAsistencia: 'A',
                rendimientoSoles: 0,
                cantidadTotal: horas,
                observaciones: null,
                producciones: consumidoresSeleccionados.map(c => ({
                  idProduccion: 0, idConsumidor: c.idConsumidor, cantidad: 0,
                })),
              });
            }
          } else {
            newDetalles.push({
              idDetalle: 0,
              idTrabajador: idTrab,
              idActividad: defaultActividad?.idActividad || 0,
              idLabor: 0,
              item: nextItem++,
              tipoAsistencia: 'A',
              rendimientoSoles: 0,
              cantidadTotal: horas,
              observaciones: null,
              producciones: consumidoresSeleccionados.map(c => ({
                idProduccion: 0, idConsumidor: c.idConsumidor, cantidad: 0,
              })),
            });
          }
        }

        setDetalles(prev => [...prev, ...newDetalles]);
      } catch { /* interceptor */ }
    };
    loadGroupWorkers();
  }, [idGrupoTrabajo]);

  const loadMasterData = async () => {
    try {
      const [p, t, a, l, c, g] = await Promise.all([
        PeriodoService.getAll(),
        TrabajadorService.getAll(),
        RendimientoService.getActividades(),
        RendimientoService.getLabores(),
        RendimientoService.getConsumidores(),
        RendimientoService.getGruposTrabajo(),
      ]);
      const sorted = p.sort((a: PeriodoPlanilla, b: PeriodoPlanilla) => b.anio - a.anio || b.mes - a.mes);
      setPeriodos(sorted);
      setTrabajadores(t as Trabajador[]);
      setActividades(a);
      setLabores(l);
      setConsumidores(c);
      setGrupos(g);
      if (sorted.length > 0 && !isEdit) setIdPeriodo(sorted[0].idPeriodo);

      // Load turnos from existing turno service
      try {
        const turnoRes = await http.get('/planilla/turno');
        setTurnos(turnoRes.data || []);
      } catch { setTurnos([]); }
    } catch { /* interceptor */ }
  };

  const loadDocumento = async (docId: number) => {
    try {
      const doc = await RendimientoService.getDocumentoCompleto(docId);
      setIdPeriodo(doc.idPeriodo);
      setFecha(doc.fecha.split('T')[0]);
      setIdResponsable(doc.idResponsable);
      setIdTurno(doc.idTurno);
      setIdGrupoTrabajo(doc.idGrupoTrabajo);
      setObservaciones(doc.observaciones || '');
      setEstado(doc.estado);

      if (doc.idResponsable && doc.responsable) {
        setResponsableObj({
          idTrabajador: doc.responsable.idTrabajador,
          personal: doc.responsable.personal,
        } as Trabajador);
      }

      // Rebuild detalles from loaded data
      const loadedConsumidorIds = new Set<number>();
      const dets: SaveRendimientoDetalle[] = (doc.detalles || []).map(d => {
        const prods: SaveProduccion[] = (d.producciones || []).map(p => {
          loadedConsumidorIds.add(p.idConsumidor);
          return { idProduccion: p.idProduccion, idConsumidor: p.idConsumidor, cantidad: p.cantidad };
        });
        return {
          idDetalle: d.idDetalle,
          idTrabajador: d.idTrabajador,
          idActividad: d.idActividad,
          idLabor: d.idLabor,
          item: d.item,
          tipoAsistencia: d.tipoAsistencia,
          rendimientoSoles: d.rendimientoSoles,
          cantidadTotal: d.cantidadTotal,
          observaciones: d.observaciones,
          producciones: prods,
        };
      });
      setDetalles(dets);

      // Set consumidores seleccionados from loaded producciones
      const allConsumidores = await RendimientoService.getConsumidores();
      setConsumidoresSeleccionados(
        allConsumidores.filter(c => loadedConsumidorIds.has(c.idConsumidor))
      );
    } catch { /* interceptor */ }
  };

  // Get hours worked from attendance for a worker on the document date
  const getHorasTrabajadas = (idTrabajador: number): number => {
    const asist = asistencias.find(a =>
      a.idTrabajador === idTrabajador && a.fecha.split('T')[0] === fecha
    );
    return asist?.horasTrabajadas || 0;
  };

  const handleAddWorker = () => {
    if (!selectedWorker) return;
    const defaultActividad = actividades.find(a => a.activo);
    const defaultLabores = defaultActividad
      ? labores.filter(l => l.idActividad === defaultActividad.idActividad && l.activo)
      : [];

    const horas = getHorasTrabajadas(selectedWorker.idTrabajador);

    // Add 3 lines per worker (JORNADA NORMAL, SOBRETIEMPO 25%, SOBRETIEMPO 35%)
    const nextItem = detalles.length > 0 ? Math.max(...detalles.map(d => d.item)) + 1 : 1;
    const newDetalles: SaveRendimientoDetalle[] = defaultLabores.slice(0, 3).map((lab, i) => ({
      idDetalle: 0,
      idTrabajador: selectedWorker.idTrabajador,
      idActividad: defaultActividad?.idActividad || 0,
      idLabor: lab.idLabor,
      item: nextItem + i,
      tipoAsistencia: 'A',
      rendimientoSoles: 0,
      cantidadTotal: horas,
      observaciones: null,
      producciones: consumidoresSeleccionados.map(c => ({
        idProduccion: 0, idConsumidor: c.idConsumidor, cantidad: 0,
      })),
    }));

    // If no default labores, add one empty line
    if (newDetalles.length === 0) {
      newDetalles.push({
        idDetalle: 0,
        idTrabajador: selectedWorker.idTrabajador,
        idActividad: defaultActividad?.idActividad || 0,
        idLabor: 0,
        item: nextItem,
        tipoAsistencia: 'A',
        rendimientoSoles: 0,
        cantidadTotal: horas,
        observaciones: null,
        producciones: consumidoresSeleccionados.map(c => ({
          idProduccion: 0, idConsumidor: c.idConsumidor, cantidad: 0,
        })),
      });
    }

    setDetalles(prev => [...prev, ...newDetalles]);
    setSelectedWorker(null);
    setAddWorkerOpen(false);
  };

  const handleDetalleChange = (index: number, field: string, value: any) => {
    setDetalles(prev => {
      const updated = [...prev];
      (updated[index] as any)[field] = value;
      // Reset labor when actividad changes
      if (field === 'idActividad') updated[index].idLabor = 0;
      return updated;
    });
  };

  const handleProduccionChange = (detalleIdx: number, idConsumidor: number, cantidad: number) => {
    setDetalles(prev => {
      const updated = [...prev];
      const det = { ...updated[detalleIdx] };
      const prods = [...det.producciones];
      const pIdx = prods.findIndex(p => p.idConsumidor === idConsumidor);
      if (pIdx >= 0) {
        prods[pIdx] = { ...prods[pIdx], cantidad };
      } else {
        prods.push({ idProduccion: 0, idConsumidor, cantidad });
      }
      det.producciones = prods;
      updated[detalleIdx] = det;
      return updated;
    });
  };

  const handleDeleteDetalle = (index: number) => {
    setDetalles(prev => prev.filter((_, i) => i !== index));
  };

  const handleToggleConsumidor = (c: Consumidor) => {
    const exists = consumidoresSeleccionados.some(x => x.idConsumidor === c.idConsumidor);
    if (exists) {
      setConsumidoresSeleccionados(prev => prev.filter(x => x.idConsumidor !== c.idConsumidor));
    } else {
      setConsumidoresSeleccionados(prev => [...prev, c]);
      // Add produccion entries for new consumidor in all detalles
      setDetalles(prev => prev.map(det => ({
        ...det,
        producciones: det.producciones.some(p => p.idConsumidor === c.idConsumidor)
          ? det.producciones
          : [...det.producciones, { idProduccion: 0, idConsumidor: c.idConsumidor, cantidad: 0 }],
      })));
    }
  };

  const handleSave = async () => {
    if (!idPeriodo) { setMsg({ text: 'Seleccione un periodo', severity: 'error' }); return; }
    if (detalles.length === 0) { setMsg({ text: 'Agregue al menos un trabajador', severity: 'error' }); return; }

    setLoading(true);
    try {
      const request: SaveRendimientoRequest = {
        idAsistenciaRendimiento: isEdit ? Number(id) : 0,
        idPeriodo,
        fecha,
        idResponsable,
        idTurno,
        idGrupoTrabajo,
        observaciones: observaciones || null,
        detalles: detalles.map((d, i) => ({
          ...d,
          item: i + 1,
          cantidadTotal: horasAsistencia[d.idTrabajador]
            ? horasAsistencia[d.idTrabajador].normal + horasAsistencia[d.idTrabajador].extras25 + horasAsistencia[d.idTrabajador].extras35
            : d.cantidadTotal,
        })),
      };
      await RendimientoService.saveDocumento(request);
      setMsg({ text: 'Documento guardado exitosamente', severity: 'success' });
      setTimeout(() => navigate('/planilla/rendimiento'), 1500);
    } catch {
      setMsg({ text: 'Error al guardar', severity: 'error' });
    } finally { setLoading(false); }
  };

  const getWorkerName = (t: Trabajador) =>
    (t as any).personal?.personaContacto || `Trabajador ${t.idTrabajador}`;

  const periodoLabel = (p: PeriodoPlanilla) =>
    `${p.anio}-${String(p.mes).padStart(2, '0')} (${p.tipoPeriodo})`;

  // Build worker name map
  const trabajadorNames: Record<number, string> = {};
  trabajadores.forEach(t => {
    trabajadorNames[t.idTrabajador] = getWorkerName(t);
  });

  // Build attendance hours map for current date
  const horasAsistencia: Record<number, { normal: number; extras25: number; extras35: number }> = {};
  asistencias
    .filter(a => a.fecha.split('T')[0] === fecha)
    .forEach(a => {
      horasAsistencia[a.idTrabajador] = {
        normal: a.horasTrabajadas || 0,
        extras25: a.horasExtras25 || 0,
        extras35: a.horasExtras35 || 0,
      };
    });

  // Computed totals
  const totalTrabajadores = new Set(detalles.map(d => d.idTrabajador)).size;
  const totalRendimiento = detalles.reduce((s, d) => s + d.rendimientoSoles, 0);
  const promedioRendimiento = totalTrabajadores > 0 ? totalRendimiento / totalTrabajadores : 0;

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">
          {isEdit ? 'Editar' : 'Nuevo'} Documento de Rendimiento
        </Typography>
        <Box display="flex" gap={1}>
          {estado !== 'PENDIENTE' && (
            <Chip
              label={ESTADOS_RENDIMIENTO.find(e => e.value === estado)?.label || estado}
              sx={{ bgcolor: ESTADOS_RENDIMIENTO.find(e => e.value === estado)?.color || '#999', color: '#fff' }}
            />
          )}
        </Box>
      </Box>

      {msg && <Alert severity={msg.severity} onClose={() => setMsg(null)} sx={{ mb: 2 }}>{msg.text}</Alert>}

      {/* Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Periodo</InputLabel>
            <Select label="Periodo" value={idPeriodo}
              onChange={e => setIdPeriodo(e.target.value as number)}>
              {periodos.map(p => (
                <MenuItem key={p.idPeriodo} value={p.idPeriodo}>{periodoLabel(p)}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField size="small" label="Fecha" type="date" value={fecha}
            onChange={e => setFecha(e.target.value)} InputLabelProps={{ shrink: true }} />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Turno</InputLabel>
            <Select label="Turno" value={idTurno || ''}
              onChange={e => setIdTurno(e.target.value as number || null)}>
              <MenuItem value="">-- Ninguno --</MenuItem>
              {turnos.map(t => (
                <MenuItem key={t.idTurno} value={t.idTurno}>{t.descripcion}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Grupo Trabajo</InputLabel>
            <Select label="Grupo Trabajo" value={idGrupoTrabajo || ''}
              onChange={e => setIdGrupoTrabajo(e.target.value as number || null)}>
              <MenuItem value="">-- Ninguno --</MenuItem>
              {grupos.map(g => (
                <MenuItem key={g.idGrupoTrabajo} value={g.idGrupoTrabajo}>
                  {g.codigo} - {g.descripcion}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            size="small"
            sx={{ minWidth: 250 }}
            options={trabajadores}
            getOptionLabel={getWorkerName}
            value={responsableObj}
            onChange={(_, v) => {
              setResponsableObj(v);
              setIdResponsable(v?.idTrabajador || null);
            }}
            renderInput={params => <TextField {...params} label="Responsable" />}
          />
        </Box>
      </Paper>

      {/* Toolbar */}
      <Box display="flex" gap={1} mb={1}>
        <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => setAddWorkerOpen(true)}>
          Agregar Trabajador
        </Button>
        <Button size="small" variant="outlined" startIcon={<ViewColumn />} onClick={() => setConsumidorDialogOpen(true)}>
          Consumidores ({consumidoresSeleccionados.length})
        </Button>
        <Button size="small" variant="outlined" startIcon={<Groups />} onClick={() => setGrupoTrabajadorDialogOpen(true)}>
          Gestionar Grupo
        </Button>
        <Box flex={1} />
        <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={loading}>
          Guardar
        </Button>
        <Button variant="outlined" startIcon={<Cancel />} onClick={() => navigate('/planilla/rendimiento')}>
          Cancelar
        </Button>
      </Box>

      {/* Detail Grid */}
      <Paper sx={{ mb: 2 }}>
        <RendimientoDetalleGrid
          detalles={detalles}
          actividades={actividades}
          labores={labores}
          consumidoresSeleccionados={consumidoresSeleccionados}
          trabajadorNames={trabajadorNames}
          horasAsistencia={horasAsistencia}
          onDetalleChange={handleDetalleChange}
          onProduccionChange={handleProduccionChange}
          onDeleteDetalle={handleDeleteDetalle}
        />
      </Paper>

      {/* Footer Totals */}
      <Paper sx={{ p: 2, display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
        <Typography variant="body1">
          <strong>Nro. de Trabajadores:</strong> {totalTrabajadores}
        </Typography>
        <Typography variant="body1">
          <strong>Promedio Rendimiento:</strong> S/ {promedioRendimiento.toFixed(2)}
        </Typography>
        <Typography variant="body1">
          <strong>Total Rendimiento:</strong> S/ {totalRendimiento.toFixed(2)}
        </Typography>
      </Paper>

      {/* Add Worker Dialog */}
      <Dialog open={addWorkerOpen} onClose={() => setAddWorkerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Agregar Trabajador</DialogTitle>
        <DialogContent>
          <Autocomplete
            sx={{ mt: 1 }}
            options={trabajadores}
            getOptionLabel={getWorkerName}
            value={selectedWorker}
            onChange={(_, v) => setSelectedWorker(v)}
            renderInput={params => <TextField {...params} label="Buscar trabajador" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddWorkerOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAddWorker} disabled={!selectedWorker}>Agregar</Button>
        </DialogActions>
      </Dialog>

      {/* Grupo Trabajador Dialog */}
      <GrupoTrabajadorDialog
        open={grupoTrabajadorDialogOpen}
        onClose={() => setGrupoTrabajadorDialogOpen(false)}
        grupos={grupos}
        trabajadores={trabajadores}
        initialGrupoId={idGrupoTrabajo}
      />

      {/* Consumidor Selection Dialog */}
      <Dialog open={consumidorDialogOpen} onClose={() => setConsumidorDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Agregar o Quitar Consumidor</DialogTitle>
        <DialogContent>
          <List>
            {consumidores.filter(c => c.activo).map(c => (
              <ListItem key={c.idConsumidor} dense sx={{ cursor: 'pointer' }}
                onClick={() => handleToggleConsumidor(c)}>
                <Checkbox
                  checked={consumidoresSeleccionados.some(x => x.idConsumidor === c.idConsumidor)}
                  tabIndex={-1}
                  disableRipple
                />
                <ListItemText primary={`${c.codigo} - ${c.descripcion}`} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsumidorDialogOpen(false)} variant="contained">Aceptar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RendimientoFormPage;
